import { LIVE_BOAT_NODE_LAYOUT } from '../config/liveBoatRoles';

export function parseW(field) {
  if (!field) return 0;
  const n = typeof field.raw === 'number' ? field.raw : parseFloat(field.raw);
  return Number.isFinite(n) ? n : 0;
}

export function nodeCenter(layout) {
  return { cx: layout.x, cy: layout.y };
}

export function edgePath(fromLayout, toLayout) {
  const a = nodeCenter(fromLayout);
  const b = nodeCenter(toLayout);
  const mx = (a.cx + b.cx) / 2;
  return `M ${a.cx} ${a.cy} Q ${mx} ${a.cy} ${b.cx} ${b.cy}`;
}

export function heroForRole(roleId, role) {
  const f = role?.fields || {};
  if (roleId === 'houseBattery') {
    if (f.soc) return f.soc.formatted;
    if (f.voltage) return f.voltage.formatted;
    return '—';
  }
  if (roleId === 'inverter') {
    const mode = f.mode?.formatted || f.chargerMode?.formatted;
    if (f.power) return `${f.power.formatted}${mode ? ` · ${mode}` : ''}`;
    if (f.voltage) return f.voltage.formatted;
    return mode || '—';
  }
  if (roleId === 'shore' || roleId === 'acLoads' || roleId === 'dcLoads' || roleId === 'solar') {
    const p = f.power || f.panelPower || f.totalPower;
    return p ? p.formatted : '0 W';
  }
  if (roleId === 'freshWater' || roleId === 'wasteWater') {
    return f.level ? f.level.formatted : '—';
  }
  if (roleId === 'enginePort' || roleId === 'engineStarboard') {
    return f.rpm ? f.rpm.formatted : 'Off';
  }
  if (roleId === 'starterBattery') {
    return f.voltage ? f.voltage.formatted : '—';
  }
  if (roleId === 'navigation') {
    if (f.sog) return f.sog.formatted;
    if (f.heading) return f.heading.formatted;
    return '—';
  }
  return '—';
}

export function subsForRole(roleId, role) {
  const f = role?.fields || {};
  const lines = [];

  if (roleId === 'houseBattery') {
    if (f.voltage) lines.push(`Voltage ${f.voltage.formatted}`);
    if (f.current) lines.push(`Current ${f.current.formatted}`);
    if (f.power) lines.push(`Power ${f.power.formatted}`);
  } else if (roleId === 'shore') {
    if (f.voltage) lines.push(`L1 ${f.voltage.formatted}`);
    if (f.current) lines.push(f.current.formatted);
    if (f.frequency) lines.push(f.frequency.formatted);
  } else if (roleId === 'acLoads') {
    if (f.voltage) lines.push(f.voltage.formatted);
    if (f.frequency) lines.push(f.frequency.formatted);
  } else if (roleId === 'solar') {
    if (f.panelVoltage) lines.push(`Panel ${f.panelVoltage.formatted}`);
    if (f.current) lines.push(f.current.formatted);
    if (f.yieldToday) lines.push(`Today ${f.yieldToday.formatted}`);
  } else if (roleId === 'inverter') {
    if (f.voltage) lines.push(f.voltage.formatted);
    if (f.current) lines.push(f.current.formatted);
    if (f.currentLimit) lines.push(`Limit ${f.currentLimit.formatted}`);
  } else if (roleId === 'dcLoads') {
    if (f.power) lines.push(f.power.formatted);
  } else if (roleId === 'enginePort' || roleId === 'engineStarboard') {
    if (f.temperature) lines.push(f.temperature.formatted);
    if (f.fuelRate) lines.push(f.fuelRate.formatted);
    if (f.runTime) lines.push(f.runTime.formatted);
  } else if (roleId === 'navigation') {
    if (f.heading) lines.push(`HDG ${f.heading.formatted}`);
    if (f.depth) lines.push(`Depth ${f.depth.formatted}`);
    if (f.waterTemp) lines.push(f.waterTemp.formatted);
  } else if (roleId === 'freshWater' || roleId === 'wasteWater') {
    if (f.remaining) lines.push(f.remaining.formatted);
    if (f.capacity) lines.push(`Cap ${f.capacity.formatted}`);
  }

  return lines;
}

export function zoneBadge(zone, roles, houseSoc, engineAlarms) {
  if (zone.id === 'electrical') {
    if (houseSoc != null) return `${houseSoc.toFixed(0)}%`;
    const v = roles.houseBattery?.fields?.voltage;
    return v ? v.formatted : '—';
  }
  if (zone.id === 'helm') {
    return heroForRole('navigation', roles.navigation);
  }
  if (zone.id === 'engines') {
    if (engineAlarms?.status === 'alert') return 'Alarm';
    if (engineAlarms?.status === 'warning' || engineAlarms?.status === 'stale') return 'Warning';
    const port = heroForRole('enginePort', roles.enginePort);
    const stbd = heroForRole('engineStarboard', roles.engineStarboard);
    if (port !== 'Off' && stbd !== 'Off') return `${port} / ${stbd}`;
    if (port !== 'Off') return port;
    if (stbd !== 'Off') return stbd;
    return 'Off';
  }
  if (zone.id === 'tanks') {
    const fresh = roles.freshWater?.fields?.level;
    return fresh ? fresh.formatted : '—';
  }
  return '—';
}

export function zoneIsActive(zone, roles) {
  if (zone.id === 'electrical') {
    const keys = ['shore', 'solar', 'houseBattery', 'acLoads', 'dcLoads'];
    return keys.some((id) => Math.abs(parseW(roles[id]?.fields?.power || roles[id]?.fields?.panelPower)) >= 3);
  }
  if (zone.id === 'engines') {
    const port = parseW(roles.enginePort?.fields?.rpm);
    const stbd = parseW(roles.engineStarboard?.fields?.rpm);
    const portRpm = port < 15 ? port * 60 : port;
    const stbdRpm = stbd < 15 ? stbd * 60 : stbd;
    return portRpm > 200 || stbdRpm > 200;
  }
  if (zone.id === 'helm') {
    return Boolean(roles.navigation?.fields?.sog || roles.navigation?.fields?.heading);
  }
  if (zone.id === 'tanks') {
    return Boolean(roles.freshWater?.fields?.level || roles.wasteWater?.fields?.level);
  }
  return false;
}

export function resolveVesselImageUrl(primaryImageUrl, backendUrl) {
  if (!primaryImageUrl) return null;
  if (primaryImageUrl.startsWith('http')) return primaryImageUrl;
  const base = String(backendUrl || '').replace(/\/$/, '');
  return `${base}${primaryImageUrl}`;
}

/** Time N Hand — Victron VRM public embed (installation share link). */
export const DEFAULT_VRM_EMBED_URL =
  'https://vrm.victronenergy.com/installation/305116/embed/d33f092e';

export function isValidVrmEmbedUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return false;
  try {
    const parsed = new URL(url.trim());
    return (
      parsed.protocol === 'https:' &&
      parsed.hostname === 'vrm.victronenergy.com' &&
      /\/installation\/\d+\/embed\/[a-f0-9]+$/i.test(parsed.pathname)
    );
  } catch {
    return false;
  }
}

/**
 * VRM iframe embed URL — monitoring_config.live_boat.vrm_embed_url or vessel 1 default.
 */
export function resolveVrmEmbedUrl(monitoringConfig, vesselId) {
  const configured = monitoringConfig?.live_boat?.vrm_embed_url;
  if (isValidVrmEmbedUrl(configured)) return configured.trim();
  if (vesselId === 1 && isValidVrmEmbedUrl(DEFAULT_VRM_EMBED_URL)) {
    return DEFAULT_VRM_EMBED_URL;
  }
  return null;
}

export function vrmPortalUrlFromEmbed(embedUrl) {
  if (!isValidVrmEmbedUrl(embedUrl)) return null;
  try {
    const parsed = new URL(embedUrl);
    const match = parsed.pathname.match(/^\/installation\/(\d+)\/embed\//);
    if (!match) return null;
    return `https://vrm.victronenergy.com/installation/${match[1]}`;
  } catch {
    return null;
  }
}

export function sceneTint(sceneId) {
  switch (sceneId) {
    case 'underway':
      return 'linear-gradient(180deg, rgba(14, 165, 233, 0.35) 0%, rgba(15, 23, 42, 0.75) 100%)';
    case 'solar':
      return 'linear-gradient(180deg, rgba(245, 158, 11, 0.3) 0%, rgba(15, 23, 42, 0.75) 100%)';
    case 'shore':
      return 'linear-gradient(180deg, rgba(16, 185, 129, 0.3) 0%, rgba(15, 23, 42, 0.75) 100%)';
    default:
      return 'linear-gradient(180deg, rgba(30, 58, 138, 0.25) 0%, rgba(15, 23, 42, 0.8) 100%)';
  }
}

export { LIVE_BOAT_NODE_LAYOUT };
